import { differenceInSeconds } from "date-fns";
import { Dispatch, SetStateAction, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router";
import {
  InlineModal,
  StyledIframe
} from "./ScormWrapper.styles";
import { useNavigate } from "../../services";
import { useLanguageStore, useNavbarStore } from "../../stores";
import type { LearningActivity, Level } from "../../types";
import { getPreviousRoute } from "../../utils/general";

export function ScormWrapper({
  activityId,
  close,
  courseId,
  // fullScreen=false,
  isOpen,
  learningObjectTypology,
  learningObjectStarted,
  pathId,
  canAccessNow,
  canAccessUntil,
  setTriggerPause
} : {
  activityId: number
  close?: () => void
  courseId: string | null
  // fullScreen?: boolean
  isOpen: boolean
  learningObjectTypology: Level
  learningObjectStarted?: LearningActivity
  pathId: string | null,
   // aggiunti con il refactoring autoplay
  canAccessNow?: boolean,
  canAccessUntil?: string,
  setTriggerPause?:Dispatch<SetStateAction<boolean>>
}) {

  const modalRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  type XAPIMessage = {
    type: "XAPI_TERMINATED";
    data: {
      endpoint:string,
      headers:Record<string, string>,
      statement:unknown
    }
  }

  const uri = `${window.location.origin}/esplora/scorm/${activityId}/${learningObjectTypology}`;
  const language = useLanguageStore(state => state.language)?.id;
  const params = `courseId=${courseId || null}&rootId=${pathId || null}&lang=${language}`;
  const navbarItem = useNavbarStore(state => state.item);
  const { pathname }=useLocation();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (isOpen) {
      iframeRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (modalRef.current && !document.contains(modalRef.current)) {
        observer.disconnect();
        const previousPath =
          getPreviousRoute({
            courseId: (courseId ? Number(courseId) : undefined),
            id: activityId,
            learningObjectTypology,
            path: pathname,
            rootId: (pathId ? Number(pathId) : undefined),
            section: navbarItem
          });
        navigate(previousPath);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [activityId, learningObjectTypology, courseId, pathId, pathname, navbarItem, navigate]);


  const controller = new AbortController();

  async function handleMessage(event: MessageEvent<XAPIMessage>) {
    console.log("TEST con controller dentro finally");
    if (event.data.type === "XAPI_TERMINATED") {
      try {
        const res = await fetch(event.data.data.endpoint, {
          body: JSON.stringify(event.data.data.statement),
          headers: event.data.data.headers,
          keepalive: true,
          method: "POST",
          signal: controller.signal
        });
        console.log("xAPI response", res);
        console.log("xAPI statement sent successfully");
      } catch (error) {
        console.error("Error sending xAPI statement:", error);
      } finally {
        console.log("dentro finally");
        window.removeEventListener("message", handleMessage);
        controller.abort();

      }
    }
  }

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (learningObjectTypology === "DA_YOUTUBE") {
        handleMessage(event as MessageEvent<XAPIMessage>);
      }
      if (event.data.type === "SCORM_READY") {
        if (iframeRef.current && iframeRef.current.contentWindow && learningObjectStarted) {
          iframeRef.current.contentWindow.postMessage({
            data: learningObjectStarted,
            type: "START_OR_RESUME_DATA"
          }, window.location.origin);
        }
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [learningObjectTypology, handleMessage, learningObjectStarted]);

  const handleIframeLoad = () => {
    // We now primarily rely on SCORM_READY, but we can keep a small delay as fallback
    // if we haven't received SCORM_READY yet.
    // However, the handshake is much safer for non-Chrome browsers.
  };

  //TODO manca il tracciamento alla chiusura dello SCORM su blocco orario
  //_______Logica blocco orario SCORM__________START
  const timeUpdateHandlerScorm = () => {
    if(canAccessNow && canAccessUntil){
      const timeLeft = differenceInSeconds(new Date(canAccessUntil), new Date());
      if(timeLeft <= 0){
        close && close();
        setTriggerPause && setTriggerPause(true);
      }
    }
  };
  //timeUpdateHandlerScorm viene eseguita ogni 250 millisecondi e se il range orarario in cui l'utente può accedere allo
  //scorm è > di new Date verrà mostrato il pop up <LearningActivityBlockOverlay>

  useEffect(() => {

    if (!isOpen || learningObjectTypology !== "DA_SCORM"|| !canAccessNow || !canAccessUntil) return;
    const intervalId = setInterval(() => {
      timeUpdateHandlerScorm();
    }, 250);
    return () => clearInterval(intervalId);
  }, [isOpen,learningObjectTypology,canAccessNow,canAccessUntil]);
  //_______Logica blocco orario SCORM__________END


  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const toRestore: HTMLElement[] = [];
    let node: HTMLElement | null = modalRef.current;

    while (node && node !== document.body) {
      const parent: HTMLElement | null = node.parentElement;
      if (parent) {
        Array.from(parent.children).forEach((sibling) => {
          const el = sibling as HTMLElement;
          if (el !== node && !el.hasAttribute("inert")) {
            el.setAttribute("inert", "");
            toRestore.push(el);
          }
        });
      }
      node = parent;
    }

    return () => {
      toRestore.forEach(el => el.removeAttribute("inert"));
    };
  }, [isOpen]);

  return (
    <InlineModal $open={ isOpen } ref={ modalRef }>
      <StyledIframe
        ref={ iframeRef }
        src={ `${uri}?${params}${learningObjectStarted ? "&hasPostMessageData=true" : ""}` }
        title={ `iframe-scorm-${learningObjectTypology}` }
        onLoad={ handleIframeLoad }
      />
    </InlineModal>
  );
}
